import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { Camera, CameraSlash, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Button } from "./ui/button";

/* Live camera preview that captures a compressed JPEG data URL for attendance marking.
   ref.capture() → { selfie } | { selfie_error }. */
export const SelfieCapture = forwardRef(function SelfieCapture(_, ref) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [shot, setShot] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) { setError("Camera not supported on this device"); return; }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } }, audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (e) {
        setError(e?.name === "NotAllowedError" ? "Camera permission denied" : (e?.message || "Camera unavailable"));
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    const w = 480;
    const h = Math.round((v.videoHeight / v.videoWidth) * w);
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(v, 0, 0, w, h);
    return c.toDataURL("image/jpeg", 0.7);
  };

  useImperativeHandle(ref, () => ({
    capture() {
      if (error) return { selfie_error: error };
      const data = shot || snap();
      return data ? { selfie: data } : { selfie_error: "Camera not ready" };
    },
  }), [error, shot]);

  return (
    <div className="mt-4 border border-zinc-300 bg-zinc-950 relative overflow-hidden" data-testid="selfie-capture"
         style={{ aspectRatio: "4 / 3" }}>
      {error ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-300 text-xs gap-2 p-4 text-center"
             data-testid="selfie-error">
          <CameraSlash size={28} weight="bold" className="text-red-400" />
          {error}. Your mark will be flagged "No selfie".
        </div>
      ) : shot ? (
        <img src={shot} alt="Selfie preview" className="absolute inset-0 w-full h-full object-cover" data-testid="selfie-preview" />
      ) : (
        <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover -scale-x-100" />
      )}
      {!error && (
        <div className="absolute bottom-2 right-2 flex gap-1.5">
          {shot ? (
            <Button type="button" size="sm" variant="outline" onClick={() => setShot(null)} data-testid="selfie-retake"
                    className="rounded-none h-7 bg-white/90 border-zinc-300 font-mono uppercase tracking-widest text-[10px]">
              <ArrowCounterClockwise size={11} weight="bold" className="mr-1" /> Retake
            </Button>
          ) : (
            <Button type="button" size="sm" onClick={() => setShot(snap())} data-testid="selfie-snap"
                    className="rounded-none h-7 bg-white text-zinc-900 hover:bg-zinc-200 font-mono uppercase tracking-widest text-[10px]">
              <Camera size={11} weight="bold" className="mr-1" /> Snap
            </Button>
          )}
        </div>
      )}
      <div className="absolute top-2 left-2 label-overline text-white/80 bg-black/40 px-1.5 py-0.5 flex items-center gap-1">
        <Camera size={10} weight="bold" /> Check-in selfie
      </div>
    </div>
  );
});
